// LaneConfigCrud.jsx

import React, {
  useEffect,
  useState,
  useContext,
  useMemo,
  useCallback,
  useRef,
  memo,
} from "react";
import { useTranslation } from "react-i18next";
import "../pages.css";
import {
  MButton,
  LoadingContext,
  M_StateActions,
  MHeader,
  NavigateContext,
  MTransferList,
} from "src/M_Components";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { BufferService } from "src/M_Components/M_Utils/buffer.service";
import { isFailurePayload, apiMessage } from "src/components/utils/apiResponse";
import DynamicForm from "../../components/Forms/DynamicForm";
import EsriGenericMap from "src/components/Maps/EsriGenericMap";

/**
 * LaneConfigCrud
 *
 * Screen component responsible for creating, editing, and viewing a Lane entity.
 *
 * Responsibilities:
 * - Loads lane metadata/options (DynamicForm schema) and lane details (when editing/viewing).
 * - Persists per-tab transient state into Redux to survive navigation between tabs.
 * - Manages a transport types selection list.
 * - Integrates a map to visualize source/destination endpoints and an arrow direction overlay.
 * - Supports selecting source/destination by clicking on the map, based on nearest candidates.
 *
 * Design notes:
 * - Avoids invoking web-component actions that may not exist; map zooming is driven by ArcGIS view.goTo.
 * - Uses stable JSON memoization for heavy props (layers/locations) to reduce mapConfig churn.
 */

/* =====================================================================================================================
 * Helpers
 * ===================================================================================================================== */

/**
 * Converts a string to a camelCase-like variant by lowercasing only the first character.
 * This is used to convert API option field names (often PascalCase) into payload keys.
 *
 * @param {string} s Raw string.
 * @returns {string} String with first character lowercased.
 */
const toCamel = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);

/**
 * Determines whether a DynamicForm option represents a boolean field.
 *
 * @param {object} opt DynamicForm option descriptor.
 * @returns {boolean} True when option type is boolean.
 */
const isBoolean = (opt) => opt?.Type === "boolean" || opt?.Type === "bool";

/**
 * Normalizes transport types selection coming from the lane DTO.
 *
 * The UI can hold either:
 * - A list of transport type objects (hydrated)
 * - A list of transport type IDs (primitive values)
 *
 * This function attempts to extract a consistent selection list from the DTO,
 * preferring hydrated objects when present, otherwise falling back to ID arrays.
 *
 * @param {object} laneDto Lane DTO returned by the API.
 * @returns {Array} Selection list of objects or IDs.
 */
const normalizeSelectedTransportTypes = (laneDto) => {
  if (!laneDto) return [];

  const objs = laneDto.TransportTypes ?? laneDto.transportTypes ?? null;
  if (Array.isArray(objs) && objs.length > 0) return objs;

  const ids =
    laneDto.transportTypeIds ??
    laneDto.TransportTypeIds ??
    laneDto.TransportTypesIds ??
    laneDto.TransportTypes?.map((x) => x?.Id).filter(Boolean) ??
    [];

  return Array.isArray(ids) ? ids : [];
};

/**
 * Checks whether an array looks like an array of IDs (numbers or strings).
 *
 * @param {any} arr Candidate array.
 * @returns {boolean} True if array is non-empty and first item is number/string.
 */
const isIdArray = (arr) =>
  Array.isArray(arr) &&
  arr.length > 0 &&
  (typeof arr[0] === "number" || typeof arr[0] === "string");

/**
 * Converts a value into a number or null, treating empty strings and nullish values as null.
 *
 * @param {any} v Value to convert.
 * @returns {number|null} Parsed finite number or null.
 */
const toNumberOrNull = (v) => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Rounds a number to 6 decimals to reduce float noise.
 *
 * @param {number} n Number to round.
 * @returns {number} Rounded number when finite, otherwise original value.
 */
const round6 = (n) => {
  if (!Number.isFinite(n)) return n;
  return Math.round(n * 1_000_000) / 1_000_000;
};

/**
 * Produces a safe trimmed label string.
 *
 * @param {any} s Input value.
 * @returns {string} Trimmed string representation.
 */
const safeLabel = (s) => String(s || "").trim();

/**
 * Maintains a stable reference for a value when its JSON representation is unchanged.
 *
 * Why:
 * - The map wrapper uses a signature to detect changes and applies expensive updates.
 * - Parent components often create new arrays/objects on each render.
 * - Returning a stable reference reduces unnecessary downstream updates.
 *
 * Important limitations:
 * - Uses JSON.stringify; values that cannot be stringified will trigger a random signature,
 *   forcing updates (which is safer than caching incorrect data).
 *
 * @param {any} value Value to memoize by JSON signature.
 * @returns {any} Stable reference when JSON signature remains identical.
 */
const useStableJsonMemo = (value) => {
  const ref = useRef({ sig: "", value });

  const sig = useMemo(() => {
    try {
      return JSON.stringify(value);
    } catch {
      return String(Math.random());
    }
  }, [value]);

  if (ref.current.sig !== sig) {
    ref.current = { sig, value };
  }

  return ref.current.value;
};

/* =====================================================================================================================
 * Geometry: Arrow representation (JSON)
 * ===================================================================================================================== */

/**
 * Builds a lightweight arrow representation between two endpoints using JSON layer definitions.
 *
 * Output shape:
 * - polyline: shaft of the arrow
 * - polygon: triangular head of the arrow
 *
 * Coordinates:
 * - Uses EPSG:4326 (longitude/latitude).
 *
 * @param {{lon:number, lat:number}} src Source endpoint.
 * @param {{lon:number, lat:number}} dst Destination endpoint.
 * @returns {object|null} JSON definitions suitable for the map wrapper, or null when invalid.
 */
const makeArrowJsonArrow = (src, dst) => {
  if (!src || !dst) return null;

  const aLon = Number(src.lon);
  const aLat = Number(src.lat);
  const bLon = Number(dst.lon);
  const bLat = Number(dst.lat);

  if (
    !Number.isFinite(aLon) ||
    !Number.isFinite(aLat) ||
    !Number.isFinite(bLon) ||
    !Number.isFinite(bLat)
  ) {
    return null;
  }

  // Direction vector and length.
  const dx = bLon - aLon;
  const dy = bLat - aLat;
  const len = Math.hypot(dx, dy);
  if (!Number.isFinite(len) || len < 1e-8) return null;

  // Unit direction vector.
  const ux = dx / len;
  const uy = dy / len;

  // Arrowhead size: proportional with bounds to keep it visible at different distances.
  const headLen = Math.max(0.25, Math.min(1.2, len * 0.1));
  const headWidth = headLen * 0.9;

  // Base of the arrowhead.
  const baseLon = bLon - ux * headLen;
  const baseLat = bLat - uy * headLen;

  // Perpendicular direction (for arrowhead width).
  const px = -uy;
  const py = ux;

  const leftLon = baseLon + px * headWidth * 0.5;
  const leftLat = baseLat + py * headWidth * 0.5;

  const rightLon = baseLon - px * headWidth * 0.5;
  const rightLat = baseLat - py * headWidth * 0.5;

  // Shaft is a single segment from source to arrowhead base.
  const shaftPath = [[[aLon, aLat], [baseLon, baseLat]]];

  // Head is a closed ring polygon.
  const headRing = [
    [[leftLon, leftLat], [bLon, bLat], [rightLon, rightLat], [leftLon, leftLat]],
  ];

  return {
    polyline: {
      type: "polyline",
      epsg: 4326,
      graphics: [{ geometry: shaftPath, attributes: { id: "lane-arrow-shaft" } }],
    },
    polygon: {
      type: "polygon",
      epsg: 4326,
      graphics: [{ geometry: headRing, attributes: { id: "lane-arrow-head" } }],
    },
  };
};

/**
 * Computes an extent that encloses two endpoints, with padding.
 *
 * ArcGIS expects:
 * - xmin, ymin, xmax, ymax
 * - spatialReference
 *
 * Padding:
 * - Uses relative padding factors derived from dx/dy, with small minimums.
 *
 * @param {{lon:number, lat:number}} src Source endpoint.
 * @param {{lon:number, lat:number}} dst Destination endpoint.
 * @param {number} padFactor Relative padding factor.
 * @returns {object} ArcGIS extent object.
 */
const computeExtentFromEndpoints = (src, dst, padFactor = 0.18) => {
  const minLon = Math.min(src.lon, dst.lon);
  const maxLon = Math.max(src.lon, dst.lon);
  const minLat = Math.min(src.lat, dst.lat);
  const maxLat = Math.max(src.lat, dst.lat);

  const dx = maxLon - minLon || 0.01;
  const dy = maxLat - minLat || 0.01;

  const padX = dx * padFactor;
  const padY = dy * padFactor;

  return {
    xmin: minLon - padX,
    ymin: minLat - padY,
    xmax: maxLon + padX,
    ymax: maxLat + padY,
    spatialReference: { wkid: 4326 },
  };
};

/* =====================================================================================================================
 * Map pick: nearest-candidate selection
 * ===================================================================================================================== */

/**
 * Computes great-circle distance between two coordinates using the Haversine formula.
 *
 * @param {number} lat1 Latitude of point A.
 * @param {number} lon1 Longitude of point A.
 * @param {number} lat2 Latitude of point B.
 * @param {number} lon2 Longitude of point B.
 * @returns {number} Distance in kilometers.
 */
const haversineKm = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;

  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) *
    Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) ** 2;

  return 2 * R * Math.asin(Math.sqrt(a));
};

/**
 * Selects the nearest location candidate for a click point, within a maximum distance.
 *
 * Candidates must have numeric latitude/longitude properties.
 *
 * @param {number} clickLat Click latitude.
 * @param {number} clickLon Click longitude.
 * @param {Array} candidates List of candidates with latitude/longitude fields.
 * @param {number} maxKm Max distance for a match.
 * @returns {{item:any, distanceKm:number}|null} Nearest match within max distance, or null.
 */
const pickNearestLocation = (clickLat, clickLon, candidates, maxKm = 50) => {
  if (!Array.isArray(candidates) || candidates.length === 0) return null;

  let best = null;
  let bestD = Infinity;

  for (const c of candidates) {
    const lat = Number(c?.latitude);
    const lon = Number(c?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;

    const d = haversineKm(clickLat, clickLon, lat, lon);
    if (d < bestD) {
      bestD = d;
      best = c;
    }
  }

  if (!best || !Number.isFinite(bestD) || bestD > maxKm) return null;
  return { item: best, distanceKm: bestD };
};

/* =====================================================================================================================
 * Legend overlay
 * ===================================================================================================================== */

/**
 * MapLegend
 *
 * Pure UI component that displays the meaning of source/destination markers and direction line.
 * This is rendered as an absolute overlay on top of the map.
 */
const MapLegend = memo(function MapLegend({ t, hasArrow }) {
  const rowStyle = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: 12,
    lineHeight: "16px",
    color: "var(--color-black)",
    opacity: 0.92,
  };

  /**
   * Produces a round swatch style.
   *
   * @param {string} bg Background color.
   * @param {string} border Border color.
   * @returns {object} Inline CSS for a swatch.
   */
  const swatch = (bg, border) => ({
    width: 12,
    height: 12,
    borderRadius: 999,
    background: bg,
    border: `2px solid ${border}`,
    boxSizing: "border-box",
    flex: "0 0 auto",
  });

  // Direction swatch uses a small line; opacity reflects whether an arrow is present.
  const arrowSwatch = {
    width: 18,
    height: 0,
    borderTop: "2px solid var(--color-black)",
    opacity: hasArrow ? 0.95 : 0.5,
  };

  return (
    <div
      style={{
        position: "absolute",
        right: 12,
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 6,
        background: "rgba(255,255,255,0.92)",
        border: "1px solid rgba(0,0,0,0.12)",
        borderRadius: 10,
        padding: "10px 12px",
        boxShadow: "0 6px 18px rgba(0,0,0,0.10)",
        backdropFilter: "blur(2px)",
        maxWidth: 260,
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          fontWeight: 700,
          fontSize: 12,
          marginBottom: 8,
          color: "var(--color-red-dark)",
        }}
      >
        {t("Legend")}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={rowStyle}>
          <span style={swatch("rgb(20, 120, 60)", "white")} />
          <span>{t("Source")}</span>
        </div>

        <div style={rowStyle}>
          <span style={swatch("rgb(30, 80, 200)", "white")} />
          <span>{t("Destination")}</span>
        </div>

        <div style={rowStyle}>
          <span style={arrowSwatch} />
          <span>{t("Direction")}</span>
        </div>
      </div>
    </div>
  );
});

/* =====================================================================================================================
 * Map controls overlay (picking mode)
 * ===================================================================================================================== */

/**
 * MapPickControls
 *
 * UI overlay that enables selecting which endpoint should be set when clicking on the map.
 *
 * Props:
 * - pickTarget: "source" | "destination"
 * - setPickTarget: setter function
 * - disabled: disables interaction when in view mode
 * - hint: optional hint text displayed beneath the buttons
 */
const MapPickControls = memo(function MapPickControls({
  t,
  disabled,
  pickTarget,
  setPickTarget,
  hint,
}) {
  const radius = 10;

  // Base button style, designed to blend as a translucent overlay.
  const btnBase = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 10px",
    borderRadius: radius,
    border: "1px solid rgba(0,0,0,0.12)",
    background: "rgba(255,255,255,0.0)",
    color: "var(--color-black)",
    cursor: disabled ? "not-allowed" : "pointer",
    fontSize: 12,
    lineHeight: "14px",
    userSelect: "none",
    boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
    pointerEvents: disabled ? "none" : "auto",
    minWidth: 210,
    justifyContent: "flex-start",
    whiteSpace: "nowrap",
  };

  /**
   * Builds button style variant for active/inactive state.
   *
   * @param {boolean} active Whether this button is the currently selected target.
   * @returns {object} Inline CSS style.
   */
  const btn = (active) => ({
    ...btnBase,
    border: `1px solid ${active ? "var(--color-red-dark)" : "rgba(0,0,0,0.12)"
      }`,
    background: active ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,0.0)",
  });

  return (
    <div
      style={{
        position: "absolute",
        right: 12,
        bottom: 12,
        zIndex: 6,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        alignItems: "stretch",
      }}
    >
      <div
        style={btn(pickTarget === "source")}
        onClick={() => setPickTarget("source")}
        role="button"
        tabIndex={0}
        onKeyDown={() => { }}
        aria-disabled={disabled}
      >
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: 999,
            background: "rgb(20, 120, 60)",
            border: "2px solid white",
            boxSizing: "border-box",
          }}
        />
        <span>{t("ClickToPickSource")}</span>
      </div>

      <div
        style={btn(pickTarget === "destination")}
        onClick={() => setPickTarget("destination")}
        role="button"
        tabIndex={0}
        onKeyDown={() => { }}
        aria-disabled={disabled}
      >
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: 999,
            background: "rgb(30, 80, 200)",
            border: "2px solid white",
            boxSizing: "border-box",
          }}
        />
        <span>{t("ClickToPickDestination")}</span>
      </div>

      {hint ? (
        <div
          style={{
            background: "rgba(255,255,255,0.92)",
            border: "1px solid rgba(0,0,0,0.12)",
            borderRadius: radius,
            padding: "8px 10px",
            boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
            color: "rgba(0,0,0,0.80)",
            fontSize: 12,
            lineHeight: "16px",
            textAlign: "center",
            maxWidth: 240,
            pointerEvents: "none",
          }}
        >
          {hint}
        </div>
      ) : null}
    </div>
  );
});

/* =====================================================================================================================
 * Memoized Map Panel
 * ===================================================================================================================== */

/**
 * MapPanel
 *
 * Wrapper that hosts the EsriGenericMap and overlays UI controls/legend.
 * Memoization avoids unnecessary re-renders when parent updates unrelated state.
 */
const MapPanel = memo(function MapPanelInner({
  mode,
  locale,
  widgets,
  layers,
  center,
  zoom,
  constraints,
  onComponentReady,
  t,
  showPickControls,
  pickControlsProps,
  legendHasArrow,
  locations,
  showLocations,
  onMapClick,
}) {
  return (
    <div
      style={{
        height: "clamp(850px, 55vh, 850px)",
        minHeight: 320,
        border: "2px solid var(--color-red-dark)",
        borderRadius: 10,
        overflow: "hidden",
        position: "relative",
        boxSizing: "border-box",
        contain: "layout paint",
        background: "var(--color-white)",
      }}
    >
      <MapLegend t={t} hasArrow={legendHasArrow} />
      {showPickControls ? (
        <MapPickControls t={t} {...pickControlsProps} />
      ) : null}

      <EsriGenericMap
        mode={mode}
        locale={locale}
        widgets={widgets}
        layers={layers}
        center={center}
        zoom={zoom}
        constraints={constraints}
        onComponentReady={onComponentReady}
        showLocations={
          showLocations && Array.isArray(locations) && locations.length > 0
        }
        locations={locations}
        enableLocationPick={false}
        onLocationPick={undefined}
        featureInfoLayers={[]}
        onFeatureInfo={undefined}
        onMapClick={onMapClick}
        configDebounceMs={250}
      />
    </div>
  );
});

/* =====================================================================================================================
 * LaneConfigCrud
 * ===================================================================================================================== */

/**
 * LaneConfigCrud
 *
 * Main screen component.
 *
 * Data flow:
 * - Reads the current tab context and operation from Redux.
 * - Loads form schema (options) and lane details based on operation and selected lane.
 * - Maintains local state for form values and map interaction.
 * - Persists state snapshots back into Redux to support tab switching.
 */
export default function LaneConfigCrud({ handleTabClose, toastMessages }) {
  const dispatch = useDispatch();
  const M_State = useSelector((state) => state.StateKeyValue);

  const { t, i18n } = useTranslation();
  const ctx = useContext(LoadingContext);
  const { renameTab } = useContext(NavigateContext);

  // Tab context and externally provided operation/line payload, commonly set by navigation actions.
  const selectedTab =
    M_State.find((obj) => obj.key === "selectedTab")?.value || "lane";
  const globalOperation = M_State.find((obj) => obj.key === "operation")?.value;
  const globalLine = M_State.find((obj) => obj.key === "LaneLine")?.value;

  // Local state is managed per-tab and persisted into Redux snapshots.
  const [id, setId] = useState(selectedTab);
  const [operation, setOperation] = useState(globalOperation ?? "Error");
  const [sessionLine, setSessionLine] = useState(globalLine ?? {});
  const [laneInfo, setLaneInfo] = useState({});
  const [isFirst, setIsFirst] = useState(true);
  const [laneId, setLaneId] = useState(globalLine?.Id ?? null);

  // DynamicForm options and TransportTypes list used by MTransferList.
  const [allOptions, setAllOptions] = useState([]);
  const [transportTypes, setTransportTypes] = useState([]);

  // Candidate lists for map-click picking; sourced from FK option results.
  const [sourcePickOptions, setSourcePickOptions] = useState([]);
  const [destinationPickOptions, setDestinationPickOptions] = useState([]);

  // Stores the initial FK query strings per field, improving UX when reopening tabs.
  const [fkInitialQueries, setFkInitialQueries] = useState({});

  // DynamicForm API ref to read current values and trigger submit/reset.
  const formRef = useRef(null);

  // Refs mirror state to avoid stale closure issues inside timers and async callbacks.
  const idRef = useRef(id);
  const operationRef = useRef(operation);
  const laneIdRef = useRef(laneId);
  const laneInfoRef = useRef(laneInfo);
  const sessionLineRef = useRef(sessionLine);

  const allOptionsRef = useRef(allOptions);
  const transportTypesRef = useRef(transportTypes);
  const sourcePickOptionsRef = useRef(sourcePickOptions);
  const destinationPickOptionsRef = useRef(destinationPickOptions);
  const fkInitialQueriesRef = useRef(fkInitialQueries);

  // Keep refs in sync with state updates.
  useEffect(() => void (idRef.current = id), [id]);
  useEffect(() => void (operationRef.current = operation), [operation]);
  useEffect(() => void (laneIdRef.current = laneId), [laneId]);
  useEffect(() => void (laneInfoRef.current = laneInfo), [laneInfo]);
  useEffect(() => void (sessionLineRef.current = sessionLine), [sessionLine]);

  useEffect(() => void (allOptionsRef.current = allOptions), [allOptions]);
  useEffect(
    () => void (transportTypesRef.current = transportTypes),
    [transportTypes]
  );
  useEffect(
    () => void (sourcePickOptionsRef.current = sourcePickOptions),
    [sourcePickOptions]
  );
  useEffect(
    () => void (destinationPickOptionsRef.current = destinationPickOptions),
    [destinationPickOptions]
  );
  useEffect(
    () => void (fkInitialQueriesRef.current = fkInitialQueries),
    [fkInitialQueries]
  );

  /**
   * tabTokenRef
   *
   * Sequence token used to ignore stale async results when tab changes.
   * Incrementing this token invalidates in-flight requests bound to older tab states.
   */
  const tabTokenRef = useRef(0);
  useEffect(() => {
    tabTokenRef.current += 1;
  }, [id]);

  // BufferService instances are memoized per base URL.
  const svcLanes = useMemo(
    () => new BufferService("Lanes", ctx.DEFAULTURL),
    [ctx.DEFAULTURL]
  );
  const svcCreate = useMemo(
    () => new BufferService("Lanes/Create", ctx.DEFAULTURL),
    [ctx.DEFAULTURL]
  );
  const svcUpdate = useMemo(
    () => new BufferService("Lanes/Update", ctx.DEFAULTURL),
    [ctx.DEFAULTURL]
  );
  const svcTransportTypes = useMemo(
    () => new BufferService("TransportTypes", ctx.DEFAULTURL),
    [ctx.DEFAULTURL]
  );
  const svcLocations = useMemo(
    () => new BufferService("Locations", ctx.DEFAULTURL),
    [ctx.DEFAULTURL]
  );

  /**
   * Normalizes operation strings into a stable set of values used by this component.
   *
   * @returns {"create"|"edit"|"view"} Normalized operation.
   */
  const normalizedOperation = useMemo(() => {
    const raw = String(operation || "").trim();
    const low = raw.toLowerCase();

    if (low === "create") return "create";
    if (low === "edit" || low === "update") return "edit";
    if (low === "view") return "view";

    if (raw === "Create") return "create";
    if (raw === "Edit") return "edit";
    if (raw === "View") return "view";

    return low || "view";
  }, [operation]);

  // View mode disables edits and map-picking.
  const isViewMode = normalizedOperation === "view";

  /**
   * Maps screen operation to EsriGenericMap mode.
   * - create -> create
   * - edit -> update
   * - view -> view
   */
  const mapMode = useMemo(() => {
    if (normalizedOperation === "create") return "create";
    if (normalizedOperation === "edit") return "update";
    return "view";
  }, [normalizedOperation]);

  /**
   * Produces a locale string compatible with the map component.
   *
   * @returns {string} Locale in a regional format.
   */
  const mapLocale = useMemo(() => {
    const lng = String(i18n.language || "pt").toLowerCase();
    if (lng.startsWith("pt")) return "pt-PT";
    if (lng.startsWith("es")) return "es-ES";
    return "en-US";
  }, [i18n.language]);

  /**
   * Updates the tab label according to operation and lane endpoints names.
   * This provides contextual naming for multi-tab workflows.
   */
  useEffect(() => {
    if (!selectedTab || !operation) return;

    const key =
      normalizedOperation === "create"
        ? "LabelNewLane"
        : normalizedOperation === "edit"
          ? "LabelEditLane"
          : "LabelViewLane";

    const src = sessionLine?.SourceLocation ?? laneInfo?.SourceLocation ?? "";
    const dst =
      sessionLine?.DestinationLocation ?? laneInfo?.DestinationLocation ?? "";
    const name = src && dst ? `${src} -> ${dst}` : "";

    const base = t(key);
    const label =
      normalizedOperation === "create" || !name ? base : `${base} "${name}"`;

    renameTab(String(selectedTab), label);
  }, [
    selectedTab,
    operation,
    normalizedOperation,
    sessionLine?.SourceLocation,
    sessionLine?.DestinationLocation,
    laneInfo?.SourceLocation,
    laneInfo?.DestinationLocation,
    renameTab,
    t,
  ]);

  /**
   * Persists a snapshot of the tab state into Redux with a debounce.
   *
   * Why:
   * - Switching tabs should preserve user progress and fetched lists.
   * - Debouncing reduces store churn and avoids storing intermediate keystrokes too frequently.
   */
  const persistTimerRef = useRef(null);
  useEffect(() => {
    if (!id) return;

    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);

    persistTimerRef.current = setTimeout(() => {
      const currentFormValues = formRef.current?.getValues?.() || {};

      // In view mode, the stored laneInfo should remain the authoritative object.
      // In edit/create, merge current form values to preserve in-progress edits.
      const infoToPersist =
        normalizedOperation === "view"
          ? laneInfoRef.current
          : { ...laneInfoRef.current, ...currentFormValues };

      dispatch(
        M_StateActions.addM_State({
          key: idRef.current,
          value: [
            { key: "laneInfo", value: infoToPersist },
            { key: "fkInitialQueries", value: fkInitialQueriesRef.current },
            { key: "isFirst", value: isFirst },
            { key: "operation", value: operationRef.current },
            { key: "sessionLine", value: sessionLineRef.current },
            { key: "allOptions", value: allOptionsRef.current },
            { key: "transportTypes", value: transportTypesRef.current },
            { key: "sourcePickOptions", value: sourcePickOptionsRef.current },
            { key: "destinationPickOptions", value: destinationPickOptionsRef.current },
          ],
        })
      );
    }, 220);

    return () => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    };
  }, [
    id,
    laneInfo,
    fkInitialQueries,
    isFirst,
    operation,
    sessionLine,
    allOptions,
    transportTypes,
    sourcePickOptions,
    destinationPickOptions,
    dispatch,
    normalizedOperation,
  ]);

  /**
   * Fetches lane details by ID and normalizes transport type selection.
   *
   * Stale request protection:
   * - Uses tabTokenRef to ensure results are applied only for the current tab state.
   *
   * @param {number|string} idToGet Lane ID.
   */
  const fetchDetails = useCallback(
    async (idToGet) => {
      const myToken = tabTokenRef.current;

      try {
        const res = await svcLanes.getId(idToGet);

        if (isFailurePayload(res)) {
          toastMessages("error", "", apiMessage(res, t("ErrorLoadingData")));
          return;
        }

        if (myToken !== tabTokenRef.current) return;

        const normalizedSelection = normalizeSelectedTransportTypes(res);

        setLaneInfo({ ...(res || {}), transportTypeIds: normalizedSelection });
        setLaneId(res?.Id ?? null);
      } catch (err) {
        toastMessages("error", "", apiMessage(err, t("ErrorLoadingData")));
      }
    },
    [svcLanes, t, toastMessages]
  );

  /**
   * Fetches DynamicForm options for Lanes and optionally triggers a details fetch.
   *
   * @param {string} op Operation string used to decide whether details should be fetched.
   * @param {object} line Session line object potentially holding an Id.
   * @param {{skipDetails:boolean}} opts Controls whether details fetching is skipped.
   */
  const fetchOptions = useCallback(
    async (op, line, { skipDetails } = { skipDetails: false }) => {
      const myToken = tabTokenRef.current;

      try {
        const res = await svcLanes.options();

        if (isFailurePayload(res) || res?.name === "AxiosError") {
          toastMessages("error", "", apiMessage(res, t("ErrorLoadingData")));
          return;
        }

        if (myToken !== tabTokenRef.current) return;

        setAllOptions(Array.isArray(res) ? res : []);

        const mode = String(op || operationRef.current || "view").toLowerCase();
        const idToUse = line?.Id ?? sessionLineRef.current?.Id;

        if (!skipDetails && mode !== "create" && idToUse) {
          await fetchDetails(idToUse);
        }
      } catch (err) {
        toastMessages("error", "", apiMessage(err, t("ErrorLoadingData")));
      }
    },
    [fetchDetails, svcLanes, t, toastMessages]
  );

  /**
   * Loads TransportTypes list to populate the transfer list control.
   * This call is performed only in create/edit workflows.
   */
  const maybeLoadTransportTypes = useCallback(async () => {
    const op = String(operationRef.current || "").toLowerCase();
    if (op !== "create" && op !== "edit") return;

    try {
      const res = await svcTransportTypes.filter(
        "?Page=1&PageSize=200&filter=Inactive%20eq%20false"
      );
      if (isFailurePayload(res)) return;

      const items = Array.isArray(res?.Items)
        ? res.Items
        : Array.isArray(res)
          ? res
          : [];

      setTransportTypes(items);
    } catch {
      // Non-critical failure: transport types do not block the main form rendering.
    }
  }, [svcTransportTypes]);

  /**
   * Restores tab state snapshot from Redux for a given session ID.
   * This supports tab switching without refetching and preserves user edits.
   *
   * @param {string} sessionID Tab session key.
   * @returns {Promise<boolean>} True when snapshot exists and was restored.
   */
  const restoreFromTabState = useCallback(
    async (sessionID) => {
      const data = M_State.find((obj) => obj.key === sessionID)?.value;

      if (data) {
        const pick = (k) => data.find((x) => x.key === k)?.value;

        setLaneInfo(pick("laneInfo") || {});
        setFkInitialQueries(pick("fkInitialQueries") || {});
        setIsFirst(pick("isFirst") ?? false);
        setOperation(pick("operation") ?? "Error");

        const sl = pick("sessionLine") || {};
        setSessionLine(sl);
        setLaneId(sl?.Id ?? pick("laneInfo")?.Id ?? null);

        setAllOptions(
          Array.isArray(pick("allOptions")) ? pick("allOptions") : []
        );
        setTransportTypes(
          Array.isArray(pick("transportTypes")) ? pick("transportTypes") : []
        );

        setSourcePickOptions(
          Array.isArray(pick("sourcePickOptions")) ? pick("sourcePickOptions") : []
        );
        setDestinationPickOptions(
          Array.isArray(pick("destinationPickOptions"))
            ? pick("destinationPickOptions")
            : []
        );

        await maybeLoadTransportTypes();
        return true;
      }

      return false;
    },
    [M_State, maybeLoadTransportTypes]
  );

  /**
   * Boot sequence:
   * - Loads initial operation and line context.
   * - Restores state snapshot when available.
   * - Fetches options and lane details when required.
   * - Loads transport types when required.
   */
  useEffect(() => {
    const boot = async () => {
      ctx.setIsLoad(true);
      try {
        const sessionForThisTab =
          M_State.find((o) => o.key === idRef.current)?.value || [];
        const pick = (k) => sessionForThisTab.find((x) => x.key === k)?.value;

        const snapOP = pick("operation");
        const snapSL = pick("sessionLine");
        const seedOperation = globalOperation !== undefined ? globalOperation : snapOP;
        const seedLine = globalLine !== undefined ? globalLine : snapSL;

        if (seedOperation !== undefined) setOperation(seedOperation);
        if (seedLine) {
          setSessionLine(seedLine);
          setLaneId(seedLine?.Id ?? null);
        }

        const restored = await restoreFromTabState(idRef.current);

        // Remove global one-time values once they have been consumed.
        if (globalOperation !== undefined)
          dispatch(M_StateActions.removeM_State({ key: "operation" }));
        if (globalLine !== undefined)
          dispatch(M_StateActions.removeM_State({ key: "LaneLine" }));

        // Load schema and then details based on operation.
        await fetchOptions(seedOperation, seedLine, { skipDetails: true });

        const opLow = String(seedOperation || operationRef.current || "").toLowerCase();
        const laneToGet = seedLine?.Id ?? sessionLineRef.current?.Id;

        if (!restored && opLow !== "create" && laneToGet) {
          await fetchDetails(laneToGet);
        }

        await maybeLoadTransportTypes();
        setIsFirst(false);
      } finally {
        ctx.setIsLoad(false);
      }
    };

    boot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Responds to selectedTab changes:
   * - switches local tab id
   * - attempts to restore snapshot
   * - falls back to fetching options/details when no snapshot exists
   */
  useEffect(() => {
    const newId = M_State.find((obj) => obj.key === "selectedTab")?.value;
    if (!newId || newId === idRef.current) return;

    setId(newId);

    (async () => {
      ctx.setIsLoad(true);
      try {
        const restored = await restoreFromTabState(newId);
        if (!restored) {
          await fetchOptions(operationRef.current, sessionLineRef.current, {
            skipDetails: true,
          });

          const opLow = String(operationRef.current || "").toLowerCase();
          const laneToGet = sessionLineRef.current?.Id;

          if (opLow !== "create" && laneToGet) {
            await fetchDetails(laneToGet);
          }

          await maybeLoadTransportTypes();
        }
      } finally {
        ctx.setIsLoad(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [M_State]);

  /**
   * Hydrates transport type selections in edit/create mode:
   * - The API can provide IDs while the transfer list expects objects for display.
   * - When transportTypes are loaded, convert ID selection into object selection.
   */
  useEffect(() => {
    const op = String(operation || "").toLowerCase();
    if (op !== "create" && op !== "edit") return;

    const current = laneInfo?.transportTypeIds || [];
    if (!isIdArray(current)) return;
    if (!Array.isArray(transportTypes) || transportTypes.length === 0) return;

    const idSet = new Set(current.map((x) => Number(x)));
    const hydrated = transportTypes.filter((tt) => idSet.has(Number(tt?.Id)));
    if (hydrated.length === 0) return;

    setLaneInfo((prev) => ({ ...prev, transportTypeIds: hydrated }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transportTypes]);

  /**
   * Builds the API payload from DynamicForm values using the schema options.
   *
   * Rules:
   * - Boolean values are coerced to true/false.
   * - Foreign keys are coerced to numbers, with empty values mapped to null.
   * - Non-foreign key values are passed through.
   *
   * @param {object} values DynamicForm current values.
   * @returns {object} Payload object with normalized keys and values.
   */
  const buildPayloadFromValues = useCallback((values) => {
    const options = Array.isArray(allOptionsRef.current)
      ? allOptionsRef.current
      : [];
    const entries = [];

    for (const opt of options) {
      if (!opt) continue;

      // FK fields may use Header as the displayed key; align with how DynamicForm emits values.
      const keySource = opt.IsForeignKey ? opt.Header || opt.Name : opt.Name;

      // Payload keys are normalized to camel-like style used by the backend contract.
      const payloadKey = toCamel(opt.Header || keySource);

      let v;

      if (isBoolean(opt)) {
        v = !!values?.[opt.Name];
      } else if (opt.IsForeignKey) {
        const rawId = values?.[keySource];
        v = rawId == null || rawId === "" ? null : Number(rawId);
      } else {
        v = values?.[keySource];
      }

      if (v !== undefined) entries.push([payloadKey, v]);
    }

    return Object.fromEntries(entries);
  }, []);

  /**
   * Handles save operation for create/edit.
   * Validates that source and destination are different before sending the payload.
   *
   * @param {object} values DynamicForm submission values.
   */
  const onSubmitDynamic = useCallback(
    async (values) => {
      // Basic validation: endpoints must differ.
      if (
        values?.SourceLocationId &&
        values?.DestinationLocationId &&
        String(values.SourceLocationId) === String(values.DestinationLocationId)
      ) {
        toastMessages("error", "", t("SourceAndDestinationMustDiffer"));
        return;
      }

      ctx.setIsLoad(true);
      try {
        const payload = buildPayloadFromValues(values);

        // Ensure transportTypeIds is always sent as a list of IDs, regardless of UI representation.
        const raw = laneInfoRef.current?.transportTypeIds || [];
        const ids = (Array.isArray(raw) ? raw : [])
          .map((x) => (typeof x === "object" ? x?.Id : x))
          .filter((v) => v != null);

        payload.transportTypeIds = ids;

        const op = String(operationRef.current || "").toLowerCase();

        const res =
          op === "create"
            ? await svcCreate.post(payload)
            : await svcUpdate.update(laneIdRef.current, payload);

        if (isFailurePayload(res)) {
          toastMessages("error", "", apiMessage(res, t("ErrorSaving")));
          return;
        }

        toastMessages(
          "success",
          "",
          op === "create" ? t("LaneCreated") : t("LaneUpdated")
        );

        // Close the tab after successful persistence.
        handleTabClose?.(idRef.current);
      } catch (err) {
        toastMessages("error", "", apiMessage(err, t("ErrorSaving")));
      } finally {
        ctx.setIsLoad(false);
      }
    },
    [buildPayloadFromValues, ctx, handleTabClose, svcCreate, svcUpdate, t, toastMessages]
  );

  /**
   * Triggers DynamicForm submission through its exposed ref API.
   */
  const triggerFormSubmit = useCallback(() => {
    formRef.current?.submit?.();
  }, []);

  /**
   * Refresh handler that reloads form options, details, and transport types.
   * Uses a single loading boundary and ensures loading is cleared in all cases.
   */
  const onRefresh = useCallback(() => {
    ctx.setIsLoad(true);

    Promise.all([
      fetchOptions(operationRef.current, sessionLineRef.current, {
        skipDetails: false,
      }),
      maybeLoadTransportTypes(),
    ]).finally(() => ctx.setIsLoad(false));
  }, [ctx, fetchOptions, maybeLoadTransportTypes]);

  // Map configuration values.
  const mapWidgets = useMemo(() => ({ search: { position: "top-right" } }), []);
  const defaultMapCenter = useMemo(() => [-9.14, 38.72], []);
  const initialZoom = 3;
  const focusZoom = 6;
  const mapConstraints = useMemo(() => ({ minZoom: 2 }), []);

  /**
   * mapComponentRef
   *
   * Stores the map component API instance delivered by EsriGenericMap onViewReady hook.
   * This reference is used to access the underlying ArcGIS view for goTo operations.
   */
  const mapComponentRef = useRef(null);

  /**
   * locationCacheRef
   *
   * In-memory cache for resolved location details fetched by ID.
   * This reduces duplicate requests when switching between tabs or revisiting the same IDs.
   */
  const locationCacheRef = useRef(new Map());

  /**
   * Resolves a location by ID into a normalized endpoint object for mapping.
   *
   * Output fields:
   * - id, lat, lon, name, code, countryId
   *
   * @param {number|string} idValue Location ID.
   * @returns {Promise<object|null>} Normalized endpoint object or null when unavailable/invalid.
   */
  const resolveLocationById = useCallback(
    async (idValue) => {
      const idNum = toNumberOrNull(idValue);
      if (!idNum) return null;

      const cached = locationCacheRef.current.get(idNum);
      if (cached) return cached;

      try {
        const res = await svcLocations.getId(idNum);
        if (isFailurePayload(res)) return null;

        const lat = toNumberOrNull(res?.Latitude);
        const lon = toNumberOrNull(res?.Longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

        const out = {
          id: idNum,
          lat: round6(Number(lat)),
          lon: round6(Number(lon)),
          name: safeLabel(res?.Name),
          code: safeLabel(res?.Code),
          countryId: toNumberOrNull(res?.CountryId),
        };

        locationCacheRef.current.set(idNum, out);
        return out;
      } catch {
        return null;
      }
    },
    [svcLocations]
  );

  // Extract endpoint IDs from the current lane form data.
  const sourceId = useMemo(
    () => toNumberOrNull(laneInfo?.SourceLocationId),
    [laneInfo?.SourceLocationId]
  );
  const destinationId = useMemo(
    () => toNumberOrNull(laneInfo?.DestinationLocationId),
    [laneInfo?.DestinationLocationId]
  );

  /**
   * resolvedEndpoints
   *
   * Holds normalized location endpoints required for:
   * - map marker layers
   * - direction arrow geometry
   * - extent computation
   */
  const [resolvedEndpoints, setResolvedEndpoints] = useState({
    source: null,
    destination: null,
  });

  /**
   * Resolves source and destination endpoints whenever their IDs change.
   * Applies token checks to avoid stale state updates across tab switches.
   */
  useEffect(() => {
    let cancelled = false;
    const myToken = tabTokenRef.current;

    (async () => {
      const [src, dst] = await Promise.all([
        resolveLocationById(sourceId),
        resolveLocationById(destinationId),
      ]);

      if (cancelled) return;
      if (myToken !== tabTokenRef.current) return;

      setResolvedEndpoints({ source: src, destination: dst });
    })();

    return () => {
      cancelled = true;
    };
  }, [sourceId, destinationId, resolveLocationById]);

  /**
   * Map view centering:
   * - Computes an extent from source and destination.
   * - Attempts to call view.goTo(extent) only when the ArcGIS view is ready.
   * - Retries a limited number of times to handle delayed view initialization.
   *
   * Retry strategy:
   * - Short repeated delays avoid blocking render and accommodate async view readiness.
   * - A bounded retry count prevents infinite loops.
   */
  useEffect(() => {
    const src = resolvedEndpoints?.source;
    const dst = resolvedEndpoints?.destination;
    if (!src || !dst) return;

    const extent = computeExtentFromEndpoints(src, dst, 0.22);
    let cancelled = false;
    let tries = 0;

    const tryGoTo = () => {
      if (cancelled) return;

      const api = mapComponentRef.current;

      // Support multiple possible shapes of the web-component API that may expose the view.
      const view = api?.view ?? api?.mapView ?? api?.sceneView ?? api?.__view;

      if (view && view.ready && typeof view.goTo === "function") {
        const p = view.goTo(extent, { duration: 350 });
        if (p && typeof p.catch === "function") p.catch(() => { });
        return;
      }

      tries += 1;
      if (tries > 20) return;
      setTimeout(tryGoTo, 120);
    };

    tryGoTo();

    return () => {
      cancelled = true;
    };
  }, [resolvedEndpoints]);

  /**
   * pickTarget
   *
   * Defines which endpoint should be updated by map clicks:
   * - "source" updates SourceLocationId
   * - "destination" updates DestinationLocationId
   */
  const [pickTarget, setPickTarget] = useState("source");

  /**
   * Applies a foreign key selection to both component state and DynamicForm state.
   *
   * Why:
   * - laneInfo state drives the map.
   * - DynamicForm internal state must also reflect the selection to keep UI consistent.
   *
   * @param {string} fieldName Field name to update.
   * @param {number} idValue Selected FK ID.
   */
  const applyFkToForm = useCallback((fieldName, idValue) => {
    setLaneInfo((prev) => ({ ...prev, [fieldName]: idValue }));

    const api = formRef.current;
    if (!api?.getValues || !api?.reset) return;

    const current = api.getValues() || {};
    const next = { ...current, [fieldName]: idValue };

    // keepDirty/keepTouched/keepErrors preserve UX state while updating a single field.
    api.reset(next, { keepDirty: true, keepTouched: true, keepErrors: true });
  }, []);

  /**
   * Normalizes a raw Location record (as returned by FK options APIs) into a map candidate.
   *
   * @param {object} raw Raw location row.
   * @returns {object|null} Map candidate or null when coordinates are invalid.
   */
  const normalizeLocationRowToMapItem = useCallback((raw) => {
    const lat = toNumberOrNull(raw?.Latitude);
    const lon = toNumberOrNull(raw?.Longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

    return {
      id: raw?.Id,
      name: safeLabel(raw?.Name),
      code: safeLabel(raw?.Code),
      latitude: round6(lat),
      longitude: round6(lon),
      countryId: toNumberOrNull(raw?.CountryId),
    };
  }, []);

  /**
   * activePickCandidates
   *
   * Candidate list used to match clicks to nearest known locations.
   * Candidates are derived from FK option lists emitted by DynamicForm.
   */
  const activePickCandidates = useMemo(() => {
    const rawList =
      pickTarget === "destination" ? destinationPickOptions : sourcePickOptions;

    return (Array.isArray(rawList) ? rawList : [])
      .map(normalizeLocationRowToMapItem)
      .filter(Boolean);
  }, [
    destinationPickOptions,
    sourcePickOptions,
    pickTarget,
    normalizeLocationRowToMapItem,
  ]);

  /**
   * mapPickLocationsRaw
   *
   * Set of points shown on the map during picking mode:
   * - Candidate points currently available for selection
   * - Ensures current resolved endpoints are always included for context
   *
   * The output list is later stabilized via useStableJsonMemo.
   */
  const mapPickLocationsRaw = useMemo(() => {
    const out = [...(activePickCandidates || [])];

    /**
     * Ensures an endpoint exists in the output list by ID,
     * avoiding duplicates when candidates already include it.
     *
     * @param {object} ep Endpoint object in {id, lat, lon, name, code, countryId} shape.
     */
    const ensure = (ep) => {
      if (!ep?.id || !Number.isFinite(ep?.lat) || !Number.isFinite(ep?.lon))
        return;

      const exists = out.some((x) => Number(x?.id) === Number(ep.id));
      if (exists) return;

      out.push({
        id: ep.id,
        name: safeLabel(ep.name),
        code: safeLabel(ep.code),
        latitude: ep.lat,
        longitude: ep.lon,
        countryId: toNumberOrNull(ep.countryId),
      });
    };

    ensure(resolvedEndpoints?.source);
    ensure(resolvedEndpoints?.destination);

    return out.filter((x) => x?.id != null);
  }, [activePickCandidates, resolvedEndpoints]);

  /**
   * mapPickLocations
   *
   * Stable reference list of locations for map display,
   * preventing frequent mapConfig changes caused by new array identities.
   */
  const mapPickLocations = useStableJsonMemo(mapPickLocationsRaw);

  /**
   * handleMapClickPick
   *
   * Map click handler:
   * - Receives coordinates from EsriGenericMap.
   * - Finds the nearest candidate within a distance threshold.
   * - Applies selection to the corresponding FK field in the form.
   *
   * @param {{lat:any, lon:any}} param Coordinates payload from the map wrapper.
   */
  const handleMapClickPick = useCallback(
    ({ lat, lon }) => {
      if (isViewMode) return;

      const cLat = toNumberOrNull(lat);
      const cLon = toNumberOrNull(lon);
      if (!Number.isFinite(cLat) || !Number.isFinite(cLon)) return;

      const nearest = pickNearestLocation(cLat, cLon, activePickCandidates, 50);
      if (!nearest?.item?.id) return;

      const field =
        pickTarget === "destination"
          ? "DestinationLocationId"
          : "SourceLocationId";

      applyFkToForm(field, Number(nearest.item.id));
    },
    [activePickCandidates, applyFkToForm, isViewMode, pickTarget]
  );

  // SVG path used by ArcGIS simple-marker of style "path".
  const PIN_PATH =
    "M16 0C9.4 0 4 5.4 4 12c0 9.4 12 20 12 20s12-10.6 12-20C28 5.4 22.6 0 16 0zM16 17.2c-2.9 0-5.2-2.3-5.2-5.2S13.1 6.8 16 6.8s5.2 2.3 5.2 5.2-2.3 5.2-5.2 5.2z";

  /**
   * mapLayersRaw
   *
   * Produces layer definitions for:
   * - Source marker (GeoJSON point)
   * - Destination marker (GeoJSON point)
   * - Direction arrow (JSON polyline + polygon) when both endpoints are available
   *
   * The output is stabilized via useStableJsonMemo to reduce map reconfiguration churn.
   */
  const mapLayersRaw = useMemo(() => {
    const layers = [];
    const src = resolvedEndpoints?.source;
    const dst = resolvedEndpoints?.destination;

    if (src && dst) {
      // Signature used to produce unique URLs for arrow layers, aiding internal layer identity tracking.
      const sig = `lane:${round6(src.lon)},${round6(src.lat)}->${round6(
        dst.lon
      )},${round6(dst.lat)}`;

      const arrow = makeArrowJsonArrow(src, dst);

      if (arrow?.polyline) {
        layers.push({
          title: "Lane Direction (Shaft)",
          type: "json",
          json: arrow.polyline,
          url: `lane-direction-shaft:${sig}`,
          symbol: { type: "simple-line", color: [0, 0, 0, 255], width: 2 },
        });
      }

      if (arrow?.polygon) {
        layers.push({
          title: "Lane Direction (Head)",
          type: "json",
          json: arrow.polygon,
          url: `lane-direction-head:${sig}`,
          symbol: {
            type: "simple-fill",
            color: [0, 0, 0, 255],
            outline: { type: "simple-line", color: [0, 0, 0, 255], width: 0 },
          },
        });
      }
    }

    if (src) {
      layers.push({
        title: "Lane Source",
        type: "geojson",
        geojson: {
          type: "FeatureCollection",
          features: [
            {
              type: "Feature",
              geometry: { type: "Point", coordinates: [src.lon, src.lat] },
              properties: { id: "lane-source", name: src.name, code: src.code },
            },
          ],
        },
        symbol: {
          type: "simple-marker",
          style: "path",
          path: PIN_PATH,
          size: 30,
          color: [20, 120, 60, 255],
          outline: { type: "simple-line", width: 1.8, color: [255, 255, 255, 255] },
        },
      });
    }

    if (dst) {
      layers.push({
        title: "Lane Destination",
        type: "geojson",
        geojson: {
          type: "FeatureCollection",
          features: [
            {
              type: "Feature",
              geometry: { type: "Point", coordinates: [dst.lon, dst.lat] },
              properties: {
                id: "lane-destination",
                name: dst.name,
                code: dst.code,
              },
            },
          ],
        },
        symbol: {
          type: "simple-marker",
          style: "path",
          path: PIN_PATH,
          size: 30,
          color: [30, 80, 200, 255],
          outline: { type: "simple-line", width: 1.8, color: [255, 255, 255, 255] },
        },
      });
    }

    return layers;
  }, [resolvedEndpoints]);

  /**
   * mapLayers
   *
   * Stable reference version of mapLayersRaw to reduce expensive map updates.
   */
  const mapLayers = useStableJsonMemo(mapLayersRaw);

  /**
   * computedMapCenterZoom
   *
   * Computes initial center/zoom values:
   * - If both endpoints exist: center on midpoint and use an initial zoom.
   * - If only one endpoint exists: center on it with a focus zoom.
   * - Otherwise: use a default center and initial zoom.
   *
   * Note:
   * - Extent zooming is handled separately via view.goTo when both endpoints are available.
   */
  const computedMapCenterZoom = useMemo(() => {
    const src = resolvedEndpoints?.source;
    const dst = resolvedEndpoints?.destination;

    if (src && dst) {
      const midLon = (Number(src.lon) + Number(dst.lon)) / 2;
      const midLat = (Number(src.lat) + Number(dst.lat)) / 2;

      if (Number.isFinite(midLon) && Number.isFinite(midLat)) {
        return { center: [midLon, midLat], zoom: initialZoom };
      }
    }

    const single = dst || src;
    if (single && Number.isFinite(single.lon) && Number.isFinite(single.lat)) {
      return { center: [single.lon, single.lat], zoom: focusZoom };
    }

    return { center: defaultMapCenter, zoom: initialZoom };
  }, [defaultMapCenter, focusZoom, initialZoom, resolvedEndpoints]);

  /**
   * Receives the map component API from EsriGenericMap and stores it for later interactions.
   *
   * @param {any} componentOrEl Map component API or element reference.
   */
  const handleMapReady = useCallback((componentOrEl) => {
    mapComponentRef.current = componentOrEl;
  }, []);

  /**
   * Handles DynamicForm value changes and related metadata messages.
   *
   * Supported meta messages:
   * - fk-query: captures last typed query per FK field to restore later
   * - fk-options: captures candidate option rows for source/destination map picking
   *
   * @param {object} patch Changed values patch produced by DynamicForm.
   * @param {object} meta Metadata describing the cause and optional data.
   */
  const handleFormValuesChange = useCallback((patch, meta) => {
    if (!idRef.current) return;

    // Track FK query strings to preserve search state when reopening the tab.
    if (meta?.cause === "fk-query" && meta?.name) {
      const field = String(meta.name);
      const q = String(meta.query ?? "");

      setFkInitialQueries((prev) => {
        if (prev?.[field] === q) return prev;
        return { ...(prev || {}), [field]: q };
      });

      return;
    }

    // Some DynamicForm implementations emit FK options with empty patch values.
    if (
      (!patch || Object.keys(patch).length === 0) &&
      meta?.cause === "fk-options" &&
      meta?.name
    ) {
      const field = String(meta.name).toLowerCase();
      const rows = Array.isArray(meta.items) ? meta.items : [];

      if (field === "sourcelocationid") setSourcePickOptions(rows);
      if (field === "destinationlocationid") setDestinationPickOptions(rows);

      return;
    }

    if (!patch || Object.keys(patch).length === 0) return;

    // Merge patch into laneInfo state, which drives map rendering and persisted snapshots.
    setLaneInfo((prev) => ({ ...prev, ...patch }));

    // Also capture candidate FK rows when provided alongside a patch.
    if (meta?.cause === "fk-options" && meta?.name) {
      const field = String(meta.name).toLowerCase();
      const rows = Array.isArray(meta.items) ? meta.items : [];

      if (field === "sourcelocationid") setSourcePickOptions(rows);
      if (field === "destinationlocationid") setDestinationPickOptions(rows);
    }
  }, []);

  /**
   * Normalizes the MTransferList value to always be an array.
   */
  const transferListValue = Array.isArray(laneInfo?.transportTypeIds)
    ? laneInfo.transportTypeIds
    : [];

  // Layout styling configuration.
  const layoutOuterBox = {
    border: "2px solid var(--color-red-dark)",
    borderRadius: 12,
    padding: 20,
    background: "var(--color-white)",
  };

  /**
   * Map candidate locations are shown only during create/edit to prevent unnecessary map updates in view mode.
   * View mode already renders endpoint markers via mapLayers.
   */
  const mapLocationsToShow = useMemo(
    () => (!isViewMode ? mapPickLocations : []),
    [isViewMode, mapPickLocations]
  );

  // showLocations determines whether the EsriGenericMap internal "Locations" overlay is enabled.
  const showLocations = !isViewMode;

  return (
    <>
      <MHeader
        element={
          <>
            <MButton
              tooltip={t("RefreshTable")}
              icon={<i className="pi pi-refresh iconTable"></i>}
              onClick={onRefresh}
              label={t("RefreshTable")}
            />

            {!isViewMode && (
              <MButton
                tooltip={t("Save")}
                icon={<i className="pi pi-save iconTable"></i>}
                onClick={triggerFormSubmit}
                label={t("Save")}
              />
            )}
          </>
        }
      />

      <div style={{ padding: "20px", background: "var(--color-white)" }}>
        <div style={layoutOuterBox}>
          <div className="lane-config-layout">
            <div className="lane-config-left">
              <div className="lane-config-form">
                <DynamicForm
                  key={`df-lane-${id}-${normalizedOperation}-${laneId ?? "new"}`}
                  formRef={formRef}
                  mode={normalizedOperation}
                  schema={allOptions}
                  data={laneInfo}
                  onSubmit={onSubmitDynamic}
                  onValuesChange={handleFormValuesChange}
                  title={t("Lane Information")}
                  containerClassName="alignInputsUserForm"
                  align="vertical"
                  fkInitialQueries={fkInitialQueries}
                />
              </div>

              <div className="lane-config-transfer">
                <MTransferList
                  disabled={isViewMode}
                  header={t("TransportTypes")}
                  entity={"TransportTypes"}
                  field={"Name"}
                  valueIdField={"Id"}
                  value={transferListValue}
                  setValue={(val) =>
                    setLaneInfo((prev) => ({ ...prev, transportTypeIds: val }))
                  }
                  multiple={true}
                  options={transportTypes}
                />
              </div>
            </div>

            <div className="lane-config-map">
              <MapPanel
                mode={mapMode}
                locale={mapLocale}
                widgets={mapWidgets}
                layers={mapLayers}
                center={computedMapCenterZoom.center}
                zoom={computedMapCenterZoom.zoom}
                constraints={mapConstraints}
                onComponentReady={handleMapReady}
                t={t}
                showPickControls={!isViewMode}
                pickControlsProps={{
                  disabled: isViewMode,
                  pickTarget,
                  setPickTarget,
                }}
                legendHasArrow={
                  !!(resolvedEndpoints?.source && resolvedEndpoints?.destination)
                }
                locations={mapLocationsToShow}
                showLocations={showLocations}
                onMapClick={handleMapClickPick}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

LaneConfigCrud.propTypes = {
  handleTabClose: PropTypes.func,
  toastMessages: PropTypes.func.isRequired,
};
